/*!

=========================================================
* Argon Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import { useEffect, useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import { Tooltip, Whisper } from "rsuite";
import { useAtom } from "jotai";
import { useNavigate, useParams, Link } from "react-router-dom";
import PagesList from "../../components/Workspaces/PagesList";
import ManageMembers from "../../components/Workspaces/ManageMembers";
import WorkspaceSettings from "../../components/Workspaces/WorkspaceSettings";
import { newToastEvent } from "../../utils/ToastUtil";
import useWorkspace from "../../api/api-hooks/workspaces/useWorkspace";
import ManageCloudAppsView from "../../views/cloud/cloudApps/ManageCloudAppsView";
import { workspaceIdAtom } from "../../atoms/WorkspaceAtoms";
import useUser from "../../api/api-hooks/auth/useUser";
import useJoinWorkspace from "../../api/api-hooks/workspaces/useJoinWorkspace";
import useLeaveWorkspace from "../../api/api-hooks/workspaces/useLeaveWorkspace";
import useWorkspacePermissions from "../../hooks/useWorkspacePermissions";
import useCloudPermissions from "../../hooks/useCloudPermissions";
import AppNavbar from "../../components/AppNavbar/AppNavbar";
import ManageTags from "../../components/Tags/ManageTags";
import { AppConfirm } from "../../utils/PopupUtil";
import AppSpinner from "../../elements/AppSpinner";
import AppHelmet from "../../components/AppComponents/AppHelmet";

const generateTab = (title: string, activeValue: string, disabled = false) => ({
  title,
  activeValue,
  disabled,
});

const HomeView = () => {
  const navigate = useNavigate();
  const [workspaceID, setWorkspaceID] = useAtom(workspaceIdAtom);
  const { workspace, workspaceData, partialAccess, workspaceError } =
    useWorkspace();
  const { joinWorkspace, joiningWorkspace } = useJoinWorkspace();
  const { leaveWorkspace, leavingWorkspace } = useLeaveWorkspace();
  const { user } = useUser();
  const { tenantID, tab } = useParams();
  const [leaveHovered, setLeaveHovered] = useState(false);
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();

  useEffect(() => {
    if (workspaceError && workspaceError.status === 403) {
      localStorage.removeItem("workspaceID");
      setWorkspaceID(null);

      navigate(`/clouds/${tenantID}/workspaces/`);

      newToastEvent(
        "error",
        "You don't have permission to access this workspace."
      );
    }
  }, [tenantID, navigate, workspaceError]);

  const join = () => {
    if (!workspaceID) return;
    joinWorkspace({ workspaceID });
  };

  const leave = () => {
    if (workspace.Owner === user?.id) {
      return newToastEvent(
        "error",
        "Can't leave workspace as the Owner. Make someone else the Owner first."
      );
    }
    AppConfirm({
      title: "Leaving Workspace",
      html: "Are you sure you want to leave this workspace?",
      icon: "warning",
      confirmButtonText: "Leave",
    }).then((result) => {
      if (result.isConfirmed) {
        if (!workspaceID) return;
        navigate(`/clouds/${tenantID}/workspaces/`);
        leaveWorkspace({ workspaceID });
      }
    });
  };

  if (!workspace) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <AppSpinner color="primary" style={{ height: 50, width: 50 }} />
      </div>
    );
  }

  return (
    <>
      <AppHelmet title={`${workspace?.Name} - GrowthCloud.com`} />
      <Container fluid>
        <>
          <div className="header pb-4">
            <Container fluid>
              <div className="header-body mb--3">
                <Row className="pt-4 pb-2 align-items-center">
                  <Col xs="auto" className="pr-0">
                    <Whisper
                      trigger="hover"
                      controlId="privateWorkspaceIcon"
                      placement="autoHorizontal"
                      speaker={
                        <Tooltip>
                          {workspace?.Privacy === "private"
                            ? "Private "
                            : "Public "}
                          Workspace
                        </Tooltip>
                      }
                    >
                      <i
                        className={`mdi ${
                          workspace?.Privacy === "private"
                            ? "mdi-shield-lock"
                            : "mdi-home"
                        } text-dark`}
                        style={{
                          fontSize: workspace?.Description ? 30 : 24,
                          lineHeight: 1,
                          position: "relative",
                          top: workspace?.Description ? 4 : 4,
                        }}
                      ></i>
                    </Whisper>
                  </Col>
                  <Col>
                    <h6 className="h2 mb-0">{workspace?.Name} </h6>
                    <h5 className="text-muted font-weight-500 mb--1">
                      {workspace?.Description}
                    </h5>
                  </Col>
                  <Col align="right">
                    {!workspaceData?.isMember && (
                      <Button
                        color="primary"
                        onClick={join}
                        style={{ width: 80 }}
                        disabled={
                          cPermissions.cloud_join_workspace || joiningWorkspace
                        }
                      >
                        {joiningWorkspace ? (
                          <AppSpinner
                            style={{ height: 20, width: 20 }}
                            color="white"
                          />
                        ) : (
                          "Join"
                        )}
                      </Button>
                    )}
                    {workspaceData?.isMember && (
                      <>
                        {workspace.Owner === user?.id ? (
                          <>
                            <Whisper
                              placement="autoHorizontal"
                              trigger="hover"
                              speaker={
                                <Tooltip>
                                  Transfer ownership of the workspace to leave
                                </Tooltip>
                              }
                            >
                              <div
                                onMouseOver={() => {
                                  setLeaveHovered(true);
                                }}
                                onMouseOut={() => {
                                  setLeaveHovered(false);
                                }}
                                style={{
                                  fontSize: "0.875rem",
                                  letterSpacing: "0.025em",
                                  padding: "0.625rem 1.25rem",
                                  fontWeight: 600,
                                  cursor: leaveHovered
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                className={`${
                                  leaveHovered ? "text-light" : "text-primary"
                                } d-inline-block`}
                              >
                                {leaveHovered ? (
                                  <>Leave Workspace</>
                                ) : (
                                  <>
                                    <i className="mdi mdi-check-bold pr-1"></i>{" "}
                                    Joined
                                  </>
                                )}
                              </div>
                            </Whisper>
                          </>
                        ) : (
                          <>
                            <Button
                              onMouseOver={() => {
                                setLeaveHovered(true);
                              }}
                              onMouseOut={() => {
                                setLeaveHovered(false);
                              }}
                              color={leaveHovered ? "danger" : "primary"}
                              outline
                              onClick={leave}
                              disabled={
                                !cPermissions.cloud_leave_workspace ||
                                leavingWorkspace
                              }
                            >
                              {leaveHovered || leavingWorkspace ? (
                                leavingWorkspace ? (
                                  "Leaving..."
                                ) : (
                                  "Leave Workspace"
                                )
                              ) : (
                                <>
                                  <i className="mdi mdi-check-bold pr-1"></i>{" "}
                                  Joined
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </Col>
                </Row>
                <Row
                  className={`d-flex mb-4 ${
                    workspace?.Description ? "pt-2" : ""
                  } pl-3 align-items-center`}
                  style={{ fontSize: 17, lineHeight: 1 }}
                >
                  <Breadcrumb
                    className="d-none d-md-block "
                    listClassName="breadcrumb-links"
                  >
                    <BreadcrumbItem>
                      <i
                        className="mdi mdi-cog text-primary"
                        style={{
                          fontSize: 18,
                          position: "relative",
                          top: -2,
                        }}
                      />
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                      <Link to={`/clouds/${tenantID}/workspaces/`}>
                        All Workspaces
                      </Link>
                    </BreadcrumbItem>
                    <BreadcrumbItem>{workspace.Name}</BreadcrumbItem>
                  </Breadcrumb>
                </Row>
              </div>
              {partialAccess ? (
                <>
                  <hr className="mt-2 mb-4"></hr>
                  <h3 className="text-medium">
                    Join this workspace to view the content.
                  </h3>
                </>
              ) : (
                <WorkspaceTabs
                  tab={tab}
                  tenantID={tenantID}
                  workspace={workspace}
                  wPermissions={wPermissions}
                />
              )}
            </Container>
          </div>
        </>
      </Container>
    </>
  );
};

interface WorkspaceTabsProps {
  tab?: string;
  workspace?: any;
  wPermissions?: any;
  tenantID?: string;
}

const WorkspaceTabs = ({
  tab,
  workspace,
  wPermissions,
  tenantID,
}: WorkspaceTabsProps) => {
  const navigate = useNavigate();
  const startingLink = `/clouds/${tenantID}/workspaces/${workspace?.id}/home/`;

  const tabs = useMemo(() => {
    const homeTabs = [];
    homeTabs.push(
      generateTab(
        "Settings",
        "settings",
        !wPermissions.workspace_modify_workspace
      )
    );
    homeTabs.push(generateTab("Members", "members"));
    homeTabs.push(generateTab("Apps", "apps"));
    homeTabs.push(generateTab("Pages", "pages"));
    homeTabs.push(generateTab("Tags", "tags"));

    return homeTabs;
  }, [wPermissions.workspace_modify_workspace]);

  const handleTabChange = (val: string) => {
    navigate(startingLink + val);
  };

  return (
    <>
      <AppNavbar tabs={tabs} activeTab={tab} onSelect={handleTabChange} />
      <div className="my-0 mb-4"></div>
      {tab === "pages" && <PagesList />}
      {tab === "members" && <ManageMembers />}
      {tab === "apps" && <ManageCloudAppsView />}
      {tab === "tags" && (
        <ManageTags
          title={`${workspace?.Name} Tags`}
          scope={{ Type: "Workspace", WorkspaceID: workspace?.id }}
          description={`Tags created in your workspace can be applied any record, but are only visible when you're inside the workspace.`}
          headerSize="sm"
        />
      )}
      {tab === "settings" && <WorkspaceSettings key={workspace?.id} />}
    </>
  );
};

export default HomeView;
